import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import useMediaQuery from '@mui/material/useMediaQuery'

import { SVGWrapper } from '../../../../components'
import { Search } from '../../../../assets/icons'
import { MOBILE, MOBILE_VIEWPORT } from '../../../../guided-toolbox/utils/breakpoints'
import { IconButton, RemoveIcon } from '../../styled'
import { SecurityRowList } from './SecurityRows'
import useSecuritySearch from './useSecuritySearch'

const FOOTER_HEIGHT = '44px'
const POPUP_PADDING = '13px'
// Insets that line the search row, and the pop-up hanging off it, up with the well around them
const SIDE_INSET = '9px'
const MOBILE_SIDE_INSET = '4px'

const Container = styled.div`
  position: relative;
  padding: 5px ${SIDE_INSET} 9px;

  ${MOBILE} {
    padding: 5px ${MOBILE_SIDE_INSET} 9px;
  }
`

export const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1.5px solid ${p => p.theme.lightestGray};
  padding-bottom: 5px;

  input {
    flex: 1 1 auto;
    min-width: 0;
    border: 0;
    outline: 0;
    padding: 0;
    background: none;
    font-family: inherit;
    font-size: 18px;
    line-height: 24px;
    color: ${p => p.theme.darkestGray};

    &::placeholder {
      color: ${p => p.theme.lightestGray};
      opacity: 0.65;
    }
  }
`

const Popup = styled.div`
  position: absolute;
  z-index: 100;
  top: calc(100% + 8px);
  left: ${SIDE_INSET};
  right: ${SIDE_INSET};
  background-color: #ffffff;
  border-radius: 10px;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.5);

  ${MOBILE} {
    left: ${MOBILE_SIDE_INSET};
    right: ${MOBILE_SIDE_INSET};
  }
`

const ScrollArea = styled.div`
  max-height: 330px;
  overflow-y: auto;
  padding: ${POPUP_PADDING} 0;
  /* Clear the footer by the same margin the top of the list gets, so the list sits evenly between them */
  padding-bottom: ${p =>
    p.hasFooter ? `calc(${FOOTER_HEIGHT} + ${POPUP_PADDING})` : POPUP_PADDING};

  ${MOBILE} {
    max-height: 260px;
  }
`

const PopupHeading = styled.div`
  padding: 0 20px;
  font-size: 16px;
  line-height: 22px;
  color: ${p => p.theme.mediumGray};
  font-weight: ${p => (p.bold ? 700 : 400)};
  font-style: ${p => p.italic && 'italic'};
`

const GroupHeading = styled.div`
  padding: 15px 20px 5px;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  color: ${p => p.theme.mediumGray};
`

const Footer = styled.button`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: ${FOOTER_HEIGHT};
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 20px;
  border: 0;
  border-radius: 0 0 10px 10px;
  background-color: #ffffff;
  box-shadow: ${p => p.overlapping && '0 -2px 1px 0 rgba(0, 0, 0, 0.1)'};
  font-family: inherit;
  font-size: 16px;
  color: ${p => p.theme.mediumGray};
  text-align: left;
  text-decoration: underline;
  cursor: pointer;
`

const EmptyMessage = styled.div`
  padding: 0 20px;
  font-size: 16px;
  line-height: 22px;
  color: ${p => p.theme.mediumGray};
`

/**
 * The search field with its results hanging off it in a pop-up.
 *
 * Mobile gives the search a screen of its own instead — see SecuritySearchScreen.
 */
const SecuritySearch = props => {
  const { accountType, notInvestedInvestments, onSelect } = props

  const theme = useTheme()
  const isMobile = useMediaQuery(MOBILE_VIEWPORT)
  const containerRef = useRef(null)
  const scrollRef = useRef(null)

  const [open, setOpen] = useState(true)
  const [scrollable, setScrollable] = useState(false)

  const search = useSecuritySearch({ accountType, notInvestedInvestments })
  const {
    inputRef,
    query,
    results,
    searching,
    genericAssetClasses,
    notInvestedOptions,
    content,
    focusInput,
  } = search

  // Close the pop-up when the user's attention moves elsewhere on the page
  useEffect(() => {
    const handlePointerDown = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
    }
  }, [])

  const handleChange = event => {
    setOpen(true)
    search.handleChange(event)
  }

  const resetSearch = () => {
    setOpen(true)
    search.resetSearch()
  }

  const handleBrowseGenerics = () => {
    setOpen(true)
    search.browseGenerics()
  }

  const handleBackToSearch = () => {
    setOpen(true)
    search.backToSearch()
  }

  const handleSelect = id => {
    setOpen(false)
    onSelect(id)
  }

  const handleKeyDown = event => {
    if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  const showPopup = open && content !== null

  // The footer only sets itself off from the list when there is a list running underneath it
  useLayoutEffect(() => {
    const scrollArea = scrollRef.current
    setScrollable(Boolean(scrollArea) && scrollArea.scrollHeight > scrollArea.clientHeight)
  }, [
    showPopup,
    content,
    searching,
    results,
    genericAssetClasses,
    notInvestedOptions.length,
    isMobile,
  ])

  return (
    <Container ref={containerRef} onKeyDown={handleKeyDown}>
      <InputRow>
        <SVGWrapper
          svg={Search}
          size='large'
          margin='0px'
          fill={theme.lightestGray}
          cursor='text'
          onClick={focusInput}
        />
        <input
          autoFocus
          ref={inputRef}
          name='securitySearch'
          type='text'
          autoComplete='off'
          aria-label='Find investment'
          placeholder={
            isMobile ? 'Search by name or ticker' : 'Search by investment name or ticker'
          }
          value={query}
          onChange={handleChange}
          onFocus={() => setOpen(true)}
        />
        {query.length > 0 && (
          <IconButton
            type='button'
            aria-label='Clear search'
            onClick={resetSearch}
            css={css`
              flex: 0 0 auto;
            `}>
            <RemoveIcon />
          </IconButton>
        )}
      </InputRow>

      {showPopup && (
        <Popup role='listbox'>
          <ScrollArea ref={scrollRef} hasFooter={content !== 'notInvested'}>
            {content === 'notInvested' && (
              <>
                <PopupHeading bold>Your plan&rsquo;s investment options</PopupHeading>
                <SecurityRowList securities={notInvestedOptions} onSelect={handleSelect} />
              </>
            )}

            {content === 'results' && (
              <>
                {results.length > 0 && (
                  <SecurityRowList securities={results} onSelect={handleSelect} />
                )}
                {results.length === 0 && (
                  <EmptyMessage>
                    {searching ? 'Searching…' : 'No investments match your search'}
                  </EmptyMessage>
                )}
              </>
            )}

            {content === 'generics' && (
              <>
                <PopupHeading italic>Choose from general asset classes</PopupHeading>
                {genericAssetClasses.map(({ label, securities }) => (
                  <div key={label}>
                    <GroupHeading>{label}</GroupHeading>
                    <SecurityRowList securities={securities} onSelect={handleSelect} />
                  </div>
                ))}
              </>
            )}
          </ScrollArea>

          {content === 'results' && (
            <Footer type='button' overlapping={scrollable} onClick={handleBrowseGenerics}>
              Can&rsquo;t find your investment?
            </Footer>
          )}

          {content === 'generics' && (
            <Footer type='button' overlapping={scrollable} onClick={handleBackToSearch}>
              Search by name or ticker
            </Footer>
          )}
        </Popup>
      )}
    </Container>
  )
}

export default observer(SecuritySearch)
